import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import "../styles/MemberDetails.css";
import { FaRegUserCircle } from "react-icons/fa";
import {
  MdOutlineLocationOn,
  MdUpdate,
  MdStore,
  MdOutlineAssignmentInd,
} from "react-icons/md";

function SupplierDetails() {
  const location = useLocation();
  const supplier = location.state?.supplier;
  const navigate = useNavigate();

  if (!supplier) {
    return (
      <div className="member-details-layout">
        <SideBar />
        <div className="member-details-main">
          <div style={{ padding: "30px", textAlign: "center" }}>
            <h3>No Supplier Data Available</h3>
            <p>Please go back and select a supplier to view details.</p>
            <Link to="/manage-supplier" className="back-link">
              ← Back to Manage Suppliers
            </Link>
          </div>
        </div>
      </div>
    );
  }


  const goToWallet = () => {
    navigate("/supplier-wallet", { state: { supplier } });
  };

  const goToEdit = () => {
    navigate("/edit-supplier-profile", { state: { supplier } });
  };

  // Helper function to handle missing data
  const getDisplayValue = (value, fallback = "Not specified") => {
    return value && value !== "" ? value : fallback;
  };

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        {/* Header */}
        <div className="details-header">
          <h2 style={{ width: "300px" }}>Supplier Details</h2>
          <p>You can view and manage supplier details</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-supplier" className="breadcrumb-link">
            Manage Suppliers
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Supplier Details</span>
        </div>

        {/* Tab buttons */}
        <div className="top-bar">
          <div className="top-buttons">
            <button className="tab-btn active">Personal Details</button>
            <button className="tab-btn" onClick={goToWallet}>
              Wallet Details
            </button>
          </div>
        </div>

        {/* Owner Details */}
        <div className="section">
          <div className="edit-btn-wrap">
            <button className="edit-btn" onClick={goToEdit}>
              Edit
            </button>
          </div>
          <h3>
            <FaRegUserCircle /> Owner Details
          </h3>
          <div className="section-content">
            <p>
              <strong>Owner Name:</strong> {getDisplayValue(supplier.fullName)}
            </p>
            <p>
              <strong>First Name:</strong>{" "}
              {getDisplayValue(supplier.first_name || supplier.firstName)}
            </p>
            <p>
              <strong>Last Name:</strong>{" "}
              {getDisplayValue(supplier.last_name || supplier.surname)}
            </p>
          </div>
        </div>

        {/* Business Details */}
        <div className="section">
          <h3>
            <MdStore /> Business Details
          </h3>
          <div className="section-content">
            {supplier.photo_url ? (
              <div style={{ marginBottom: "15px" }}>
                <img
                  src={supplier.photo_url}
                  alt="Owner"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #ddd",
                  }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: "15px", color: "#666" }}>
                <FaRegUserCircle size={50} />
                <p>No photo available</p>
              </div>
            )}
            <p>
              <strong>Business Name:</strong>{" "}
              {getDisplayValue(
                supplier.shop_details?.supplier_name || supplier.supplierName || supplier.retail_name
              )}
            </p>
            <p>
              <strong>Email:</strong> {getDisplayValue(supplier.email)}
            </p>
            <p>
              <strong>Mobile:</strong>{" "}
              {getDisplayValue(supplier.phone_number || supplier.mobile)}
            </p>
            <p>
              <strong>Type of Industry:</strong>{" "}
              {getDisplayValue(
                supplier.shop_details?.supplier_type || supplier.industry || supplier.retail_type
              )}
            </p>
            <p>
              <strong>Retail Sub Type:</strong>{" "}
              {getDisplayValue(
                supplier.shop_details?.supplier_sub_type || supplier.supplierSubType || supplier.retail_sub_type
              )}
            </p>
            <p>
              <strong>Website:</strong>{" "}
              {getDisplayValue(supplier.shop_details?.website || supplier.website)}
            </p>
            <p>
              <strong>Contract Signed:</strong>{" "}
              {getDisplayValue(supplier.signed_contract) ? "Yes" : "No"}
            </p>
            <p>
              <strong>Business Status:</strong>{" "}
              {getDisplayValue(supplier.status)}
            </p>
          </div>
        </div>

        {/* Business Address */}
        <div className="section">
          <h3>
            <MdOutlineLocationOn /> Business Address
          </h3>
          <div className="section-content">
            <p>
              <strong>Address line 1:</strong>{" "}
              {getDisplayValue(supplier.address_line1 || supplier.address?.line1)}
            </p>
            <p>
              <strong>Address line 2:</strong>{" "}
              {getDisplayValue(supplier.address_line2 || supplier.address?.line2)}
            </p>
            <p>
              <strong>Address line 3:</strong>{" "}
              {getDisplayValue(supplier.address_line3 || supplier.address?.line3)}
            </p>
            <p>
              <strong>City:</strong>{" "}
              {getDisplayValue(supplier.city || supplier.address?.city)}
            </p>
            <p>
              <strong>Region:</strong>{" "}
              {getDisplayValue(supplier.region || supplier.address?.county)}
            </p>
            <p>
              <strong>Postcode:</strong>{" "}
              {getDisplayValue(supplier.postcode || supplier.address?.postcode)}
            </p>
            <p>
              <strong>Country:</strong>{" "}
              {getDisplayValue(supplier.country || supplier.address?.country)}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="section">
          <h3>
            <MdUpdate /> Status
          </h3>
          <div className="section-content">
            <p>
              <strong>Email Status:</strong>
              <span
                className={`status ${(
                  supplier.current_status || supplier.status
                )?.toLowerCase()}`}
              >
                {getDisplayValue(supplier.current_status || supplier.status)}
              </span>
            </p>
            <p>
              <strong>Notes:</strong>{" "}
              {getDisplayValue(
                supplier.status_note || supplier.notes,
                "No notes available"
              )}
            </p>
          </div>
        </div>

        {/* Referred By */}
        {/* <div className="section">
          <h3>
            <MdOutlineAssignmentInd /> Referred By
          </h3>
          <div className="section-content">
            <p>
              <strong>Referrer ID:</strong> {supplier.referrer_id || "N/A"}
            </p>
            <p>
              <strong>Referrer:</strong> {supplier.referrer_name || "N/A"}
            </p>

            <p>
              <strong>Referrer Agent Type:</strong>{" "}
              {supplier.referrer_agent_type}
            </p>
            <p>
              <strong>Referrer Country:</strong>{" "}
              {supplier.referrer_country || "N/A"}
            </p>
            <p>
              <strong>Referrer Region:</strong>{" "}
              {supplier.referrer_region || "N/A"}
            </p>
            <p>
              <strong>Referrer Email:</strong> {supplier.referrer_email || "N/A"}
            </p>
            <p>
              <strong>Referrer Phone:</strong> {supplier.referrer_phone || "N/A"}
            </p>
          </div>
        </div> */}

        {/* Additional Information */}
        <div className="section">
          <h3>Additional Information</h3>
          <div className="section-content">
            <p>
              <strong>Created Date:</strong>{" "}
              {supplier.created_at
                ? new Date(supplier.created_at).toLocaleDateString()
                : "Not available"}
            </p>
            <p>
              <strong>Last Updated:</strong>{" "}
              {supplier.updated_at
                ? new Date(supplier.updated_at).toLocaleDateString()
                : "Not available"}
            </p>
            {supplier.has_keys_generated && (
              <p>
                <strong>Key Status:</strong> Keys Generated ✓
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupplierDetails;
